import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/articles';
import { getArticleById } from '@/mocks/article-details';

export default function ArticleContent() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <article className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
        <p className="text-foreground-400 mt-4">文章不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </article>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  return (
    <article className="w-full">
      {/* Hero area */}
      <div className="relative w-full pt-24 md:pt-28 pb-0">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav aria-label="面包屑导航" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-foreground-400">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  首页
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              {category && (
                <>
                  <li>
                    <Link
                      to={`/category/${category.slug}`}
                      className="hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      {category.name}
                    </Link>
                  </li>
                  <li>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </li>
                </>
              )}
              <li>
                <span className="text-foreground-600 line-clamp-1">{article.title}</span>
              </li>
            </ol>
          </nav>

          {/* Title */}
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-4 pb-6 border-b border-background-200/70">
            <span className="flex items-center gap-1.5 text-foreground-400 text-xs md:text-sm">
              <i className="ri-user-line text-sm"></i>
              {article.author}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1.5 text-foreground-400 text-xs md:text-sm">
              <i className="ri-calendar-line text-sm"></i>
              {article.date}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1.5 text-foreground-400 text-xs md:text-sm">
              <i className="ri-time-line text-sm"></i>
              {article.readTime}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="flex items-center gap-1.5 text-foreground-400 text-xs md:text-sm">
              <i className="ri-fire-line text-sm text-primary-500"></i>
              {article.heat > 9999
                ? `${(article.heat / 10000).toFixed(1)}万热度`
                : `${article.heat}热度`}
            </span>
            <span className="text-foreground-300">|</span>
            <span className="text-foreground-400 text-xs md:text-sm">来源: {article.source}</span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-4">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Cover Image */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 mt-8">
        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`911爆料网 - ${article.title}`}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Article Body */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 mt-10 pb-12">
        <div
          className="prose prose-base md:prose-lg max-w-none
            prose-headings:font-heading prose-headings:text-foreground-950
            prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-5
            prose-blockquote:border-l-primary-500 prose-blockquote:bg-background-100 prose-blockquote:py-3 prose-blockquote:px-5 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
            prose-blockquote:text-foreground-600 prose-blockquote:text-base
            prose-strong:text-foreground-900"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </div>
    </article>
  );
}